import { Component, input, signal, effect, output, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';

export interface TabItem {
  id: string;
  label: string;
  disabled?: boolean;
}

export type TabsVariant = 'default' | 'minimal';

@Component({
  selector: 'htx-tabs',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './tabs.component.html',
  styleUrl: './tabs.component.scss'
})
export class TabsComponent implements OnInit {
  /**
   * Array of tab items with id and label
   */
  tabs = input.required<TabItem[]>();

  /**
   * Variant: 'default' (with border) or 'minimal' (header only, no border)
   */
  variant = input<TabsVariant>('default');

  /**
   * Currently active tab id (public, can be used in templates with template refs)
   */
  activeTabId = signal<string>('');

  /**
   * Emits when active tab changes
   */
  tabChanged = output<string>();

  /**
   * Trigger animation on tab change
   */
  animating = signal<boolean>(false);

  constructor() {
    // Initialize with first tab when tabs change
    effect(() => {
      const tabsList = this.tabs();
      if (tabsList.length > 0 && !this.activeTabId()) {
        this.activeTabId.set(tabsList[0].id);
      }
    });
  }

  ngOnInit() {
    const firstTab = this.tabs()[0];
    if (firstTab && !this.activeTabId()) {
      this.activeTabId.set(firstTab.id);
    }
  }

  /**
   * Set active tab
   */
  selectTab(tabId: string): void {
    const tab = this.tabs().find(t => t.id === tabId);
    if (tab && !tab.disabled) {
      this.animating.set(false);
      setTimeout(() => {
        this.activeTabId.set(tabId);
        this.tabChanged.emit(tabId);
        setTimeout(() => {
          this.animating.set(true);
        }, 0);
      }, 50);
    }
  }

  /**
   * Get the active tab
   */
  get activeTab() {
    return this.tabs().find(t => t.id === this.activeTabId());
  }

  /**
   * Check if a tab is active
   */
  isTabActive(tabId: string): boolean {
    return this.activeTabId() === tabId;
  }
}
